import './index.css';
import { Link } from 'react-router-dom';

const sectionStyle = { marginBottom: '48px' };
const h2Style = { fontSize: '24px', marginBottom: '16px', color: '#14213D', marginTop: '32px' };
const h3Style = { fontSize: '20px', marginTop: '24px', marginBottom: '12px', color: '#14213D' };
const pStyle = { marginBottom: '16px', lineHeight: '1.6', color: '#475569' };
const ulStyle = { marginBottom: '24px', paddingLeft: '24px', lineHeight: '1.8', color: '#475569' };
const linkStyle = { color: '#007AFF', textDecoration: 'underline' };

const TermsAndConditions = () => {
  return (
    <main style={{ padding: '72px 0 56px', minHeight: '80vh' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        <h1 style={{ fontSize: '36px', marginBottom: '12px', color: '#14213D' }}>
          Terms and Conditions
        </h1>
        <p style={{ marginBottom: '32px', lineHeight: '1.6', color: '#64748B', fontSize: '15px' }}>
          Last updated: 7 August 2026
        </p>

        <section style={sectionStyle}>
          <h2 style={h2Style}>1. Introduction</h2>
          <p style={pStyle}>
            These Terms and Conditions (“Terms”) govern your access to and use of the Posa platform
            operated by Oxide Digital Technology (“Posa”, “we”, “us”, or “our”), including:
          </p>
          <ul style={ulStyle}>
            <li>the Posa Sport mobile application;</li>
            <li>the Posa Studio mobile application;</li>
            <li>the website at{' '}
              <a href="https://www.posasport.com" style={linkStyle}>https://www.posasport.com</a>
              {' '}(the “Website”); and
            </li>
            <li>related services we provide in connection with the above.</li>
          </ul>
          <p style={pStyle}>
            Together, these are referred to as the “Platform”.
          </p>
          <p style={pStyle}>
            By creating an account, signing in, browsing, booking, hosting, listing, or otherwise using
            the Platform, you agree to these Terms. If you do not agree, do not use the Platform.
          </p>
          <p style={pStyle}>
            These Terms apply together with our{' '}
            <Link to="/privacy-policy" style={linkStyle}>Privacy Policy</Link>.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>2. About Posa</h2>
          <p style={pStyle}>
            Posa is a platform that helps people discover, join, host, and manage sports activities,
            events, venue bookings, and trainer sessions.
          </p>
          <ul style={ulStyle}>
            <li>
              <strong>Posa Sport</strong> is intended for participants, players, trainers, and venues
              discovering and joining activities, events, venues, and trainers.
            </li>
            <li>
              <strong>Posa Studio</strong> is intended for studios, organizers, instructors, venues,
              and trainers managing schedules, activities, events, reservations, and related operations.
            </li>
          </ul>
          <p style={pStyle}>
            Posa provides technology to connect users. Unless we clearly state otherwise, Posa is not
            the organizer, host, venue operator, trainer, or provider of the sports activity, event,
            or booking listed on the Platform.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>3. Eligibility</h2>
          <p style={pStyle}>You must:</p>
          <ul style={ulStyle}>
            <li>
              be at least 16 years old, or the minimum age required in your country to use online
              services and consent to data processing;
            </li>
            <li>have the legal capacity to enter into a binding agreement; and</li>
            <li>provide accurate registration information.</li>
          </ul>
          <p style={pStyle}>
            If you use the Platform on behalf of a business, studio, venue, or organization, you
            confirm that you have authority to bind that entity to these Terms.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>4. Account registration and security</h2>
          <p style={pStyle}>
            You may sign in using supported authentication methods, including email, phone number,
            Apple, or Google, powered by Firebase Authentication as described in our{' '}
            <Link to="/privacy-policy" style={linkStyle}>Privacy Policy</Link>.
          </p>
          <p style={pStyle}>You agree to:</p>
          <ul style={ulStyle}>
            <li>provide accurate and current account information;</li>
            <li>keep your login credentials and device secure;</li>
            <li>
              notify us promptly at{' '}
              <a href="mailto:support@oxide-digital.com" style={linkStyle}>support@oxide-digital.com</a>
              {' '}if you suspect unauthorized access; and
            </li>
            <li>
              accept responsibility for activity occurring through your account, except where caused
              by our fault.
            </li>
          </ul>
          <p style={pStyle}>
            We may suspend or refuse registration where we reasonably believe information is false,
            misleading, or harmful.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>5. The Platform’s role</h2>
          <p style={pStyle}>Posa provides tools including, without limitation:</p>
          <ul style={ulStyle}>
            <li>discovery of nearby activities, events, venues, and trainers;</li>
            <li>joining activities and events;</li>
            <li>requesting and managing bookings;</li>
            <li>waitlists and spot offers;</li>
            <li>activity and booking chat;</li>
            <li>check-in features;</li>
            <li>calendar and schedule management;</li>
            <li>digital passes or wallet entries where supported;</li>
            <li>notifications and messaging.</li>
          </ul>
          <p style={pStyle}>
            Posa does not guarantee that any activity, event, venue, trainer, studio, time slot, or
            booking will be available, confirmed, safe, suitable, or as described.
          </p>
          <p style={pStyle}>
            Any agreement to participate in an activity, attend an event, use a venue, or receive
            training is primarily between you and the relevant host, studio, venue, or trainer, unless
            we clearly state that Posa is the seller or provider.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>6. User roles</h2>
          <p style={pStyle}>Depending on how you use the Platform, you may act as:</p>
          <ul style={ulStyle}>
            <li>a Participant joining activities or events;</li>
            <li>a Host creating or running sport sessions;</li>
            <li>a Trainer offering sessions;</li>
            <li>a Venue or Studio accepting bookings or publishing activities and events;</li>
            <li>an Organizer or Instructor using Posa Studio.</li>
          </ul>
          <p style={pStyle}>
            You are responsible for ensuring that your use of the Platform complies with applicable
            laws, venue rules, licensing requirements, insurance obligations, and any rules
            communicated by hosts, studios, or venues.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>7. Listings, activities, and events</h2>
          <p style={pStyle}>
            Hosts, studios, venues, and trainers are responsible for the accuracy of their listings,
            including dates and times, locations, activity type, pricing and payment instructions,
            capacity, participation requirements, cancellation rules, safety information, and any
            images or promotional content uploaded.
          </p>
          <p style={pStyle}>
            You must not post content that is false, misleading, unlawful, discriminatory, dangerous,
            infringing, or otherwise harmful.
          </p>
          <p style={pStyle}>
            We may remove, edit, restrict, or decline to display any listing or content that we
            reasonably believe violates these Terms, applicable law, or Platform policies.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>8. Bookings, reservations, and participation</h2>

          <h3 style={h3Style}>8.1 Joining activities and events</h3>
          <p style={pStyle}>
            When you join an activity or event, you agree to review the details carefully before
            participating. Hosts and organizers may set rules, capacity limits, cut-off times, and
            participation requirements.
          </p>

          <h3 style={h3Style}>8.2 Venue and trainer bookings</h3>
          <p style={pStyle}>
            Some bookings require approval from the venue, studio, or trainer. A booking may remain
            pending until approved. A pending booking is not confirmed until the relevant provider
            accepts it, unless the Platform clearly states otherwise.
          </p>

          <h3 style={h3Style}>8.3 Waitlists</h3>
          <p style={pStyle}>
            If a session or event is full, you may be able to join a waitlist. Waitlist offers, if
            any, may be time-limited. Failure to accept an offered spot within the stated period may
            result in the offer expiring or being offered to another user.
          </p>

          <h3 style={h3Style}>8.4 Check-in</h3>
          <p style={pStyle}>
            Where check-in is available, it may only open within a limited time before the scheduled
            start. You are responsible for arriving on time and complying with on-site instructions.
          </p>

          <h3 style={h3Style}>8.5 Cancellations</h3>
          <p style={pStyle}>
            Cancellation rights depend on the type of booking and the rules set by the host, studio,
            venue, or trainer, as shown in the Platform or communicated to you.
          </p>
          <p style={pStyle}>
            You may cancel where the Platform provides a cancellation option, but refunds or credits
            are not guaranteed unless expressly stated.
          </p>
          <p style={pStyle}>
            Hosts, studios, venues, and trainers may cancel or modify sessions for operational,
            safety, weather, staffing, or other legitimate reasons. We will use reasonable efforts to
            provide notifications where supported by the Platform, but we are not liable for missed
            notifications caused by device settings, network issues, or third-party services.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>9. Payments and fees</h2>
          <p style={pStyle}>
            Consistent with our{' '}
            <Link to="/privacy-policy" style={linkStyle}>Privacy Policy</Link>, unless clearly stated
            otherwise in the Platform:
          </p>
          <ul style={ulStyle}>
            <li>most bookings are paid on-site and are not processed by Posa;</li>
            <li>
              prices, deposits, and payment terms are set by the relevant host, studio, venue, or
              trainer;
            </li>
            <li>
              venue owners and trainers using Posa Studio may receive payouts through our payment
              partner, Paystack;
            </li>
            <li>
              any taxes, service charges, equipment fees, or additional venue charges are your
              responsibility unless stated otherwise.
            </li>
          </ul>
          <p style={pStyle}>
            If Posa introduces in-app payments, subscriptions, commissions, or service fees in the
            future, additional payment terms will apply and may be presented at checkout or in a
            separate fee schedule.
          </p>
          <p style={pStyle}>
            You agree not to circumvent Platform fees or booking flows where fees apply.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>10. Wallet passes and digital tickets</h2>
          <p style={pStyle}>
            Where available, the Platform may allow you to add bookings or event entries to Apple
            Wallet, Google Wallet, or similar services.
          </p>
          <p style={pStyle}>
            Wallet passes are provided for convenience only. They do not guarantee entry,
            participation, or refund rights. Admission and participation remain subject to the rules
            of the host, studio, venue, or event operator and to valid confirmation of your booking
            or registration.
          </p>
          <p style={pStyle}>
            You are responsible for keeping your device secure and ensuring pass details remain
            accurate.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>11. Communications and chat</h2>
          <p style={pStyle}>
            The Platform may provide chat, notifications, and messaging related to activities,
            bookings, or events — including chat between participants of a joined session.
          </p>
          <p style={pStyle}>
            You agree to use communications respectfully and lawfully. You must not use chat or
            notifications to harass, spam, threaten, impersonate others, share unlawful content, or
            send unsolicited commercial messages.
          </p>
          <p style={pStyle}>
            We may monitor, restrict, or remove communications where required by law or where
            necessary to enforce these Terms, protect users, or maintain Platform integrity.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>12. Acceptable use</h2>
          <p style={pStyle}>You agree not to:</p>
          <ul style={ulStyle}>
            <li>use the Platform for unlawful, fraudulent, or harmful purposes;</li>
            <li>impersonate any person or misrepresent your affiliation;</li>
            <li>
              scrape, copy, reverse engineer, or attempt to extract source code or data except as
              permitted by law;
            </li>
            <li>interfere with Platform security or operation;</li>
            <li>upload malware or harmful code;</li>
            <li>collect user data without authorization;</li>
            <li>use the Platform to arrange unsafe, unlicensed, or prohibited activities;</li>
            <li>discriminate against others in violation of applicable law;</li>
            <li>post content you do not have the right to use;</li>
            <li>circumvent booking limits, waitlist rules, or access controls.</li>
          </ul>
          <p style={pStyle}>
            We may investigate violations and take action including warnings, content removal,
            suspension, or account termination.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>13. Health, safety, and assumption of risk</h2>
          <p style={pStyle}>
            Sports and physical activities involve inherent risks, including injury, illness,
            property damage, or death.
          </p>
          <p style={pStyle}>
            By using the Platform and participating in any activity, event, training session, or
            venue booking, you voluntarily assume all such risks to the fullest extent permitted by
            law.
          </p>
          <p style={pStyle}>
            You are responsible for assessing your fitness, skill level, medical suitability,
            equipment, insurance, and compliance with local laws and venue rules before
            participating.
          </p>
          <p style={pStyle}>
            Posa does not provide medical, coaching, safety, or legal advice.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>14. Third-party services and links</h2>
          <p style={pStyle}>
            The Platform may integrate with or link to third-party services, including authentication
            and data services (such as Firebase, a service of Google LLC), payment and payout
            partners (such as Paystack), maps, wallet providers, calendar apps, and external
            websites.
          </p>
          <p style={pStyle}>
            We do not control third-party services and are not responsible for their content,
            availability, terms, or privacy practices. Your use of third-party services is at your
            own risk and subject to their terms. See our{' '}
            <Link to="/privacy-policy" style={linkStyle}>Privacy Policy</Link> for more about our
            providers.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>15. Intellectual property</h2>
          <p style={pStyle}>
            The Platform, including its design, software, branding, logos, text, graphics, and other
            materials, is owned by Oxide Digital Technology or its licensors and is protected by
            intellectual property laws. Copyright in Posa Sport, Posa Studio, and this Website belongs
            to Oxide Digital Technology.
          </p>
          <p style={pStyle}>
            You may use the Platform only for its intended purpose. You may not copy, modify,
            distribute, sell, lease, or create derivative works from the Platform except as expressly
            permitted.
          </p>
          <p style={pStyle}>
            If you upload content to the Platform, you grant Posa a non-exclusive, worldwide,
            royalty-free license to host, store, reproduce, display, and distribute that content as
            necessary to operate, promote, and improve the Platform. You confirm you have the rights
            needed to grant this license.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>16. Privacy</h2>
          <p style={pStyle}>
            Our collection and use of personal data is described in our{' '}
            <Link to="/privacy-policy" style={linkStyle}>Privacy Policy</Link>.
          </p>
          <p style={pStyle}>
            By using the Platform, you acknowledge that certain information — such as your profile
            details, bookings, participation status, and communications — may be shared with hosts,
            studios, venues, trainers, or other users as needed to provide the service, consistent
            with that Privacy Policy.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>17. Disclaimers</h2>
          <p style={pStyle}>
            To the fullest extent permitted by law, the Platform is provided “as is” and “as
            available”.
          </p>
          <p style={pStyle}>
            We do not warrant that the Platform will be uninterrupted, secure, error-free, or free
            from harmful components.
          </p>
          <p style={pStyle}>Without limiting the above, we make no warranties regarding:</p>
          <ul style={ulStyle}>
            <li>the conduct of users, hosts, trainers, venues, or studios;</li>
            <li>the quality, safety, legality, or suitability of any activity, event, or booking;</li>
            <li>the accuracy of listings, maps, locations, or availability;</li>
            <li>confirmation of bookings or waitlist offers; or</li>
            <li>compatibility with your device or third-party services.</li>
          </ul>
          <p style={pStyle}>
            Nothing in these Terms excludes or limits warranties that cannot be excluded under
            applicable law.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>18. Limitation of liability</h2>
          <p style={pStyle}>To the fullest extent permitted by law:</p>
          <ul style={ulStyle}>
            <li>
              Posa will not be liable for any indirect, incidental, special, consequential, or
              punitive damages; and
            </li>
            <li>
              Posa’s total liability arising out of or relating to the Platform or these Terms will
              not exceed the greater of EUR 100, or the amount you paid directly to Posa for the
              relevant service in the 12 months before the event giving rise to the claim.
            </li>
          </ul>
          <p style={pStyle}>
            These limits do not apply where prohibited by law, including liability for death or
            personal injury caused by negligence where such limitation is not permitted.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>19. Indemnity</h2>
          <p style={pStyle}>
            You agree to indemnify and hold harmless Oxide Digital Technology, its directors,
            employees, contractors, and affiliates from claims, losses, damages, liabilities, and
            expenses (including reasonable legal fees) arising out of:
          </p>
          <ul style={ulStyle}>
            <li>your use of the Platform;</li>
            <li>your listings, content, or communications;</li>
            <li>your participation in any activity, event, or booking;</li>
            <li>your breach of these Terms; or</li>
            <li>your violation of any law or third-party rights.</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>20. Suspension and termination</h2>
          <p style={pStyle}>You may stop using the Platform at any time.</p>
          <p style={pStyle}>
            We may suspend or terminate your access, with or without notice, if we reasonably believe
            you have violated these Terms, created risk or legal exposure for Posa or other users,
            provided false information, or misused the Platform.
          </p>
          <p style={pStyle}>
            Termination does not affect rights or obligations that reasonably should survive,
            including payment obligations, intellectual property, disclaimers, liability limits, and
            indemnity.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>21. Changes to these Terms</h2>
          <p style={pStyle}>
            We may update these Terms from time to time. When we do, we will revise the “Last
            updated” date and, where appropriate, provide notice through the Platform or Website.
          </p>
          <p style={pStyle}>
            Continued use after changes become effective constitutes acceptance of the updated
            Terms. If you do not agree, you must stop using the Platform.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>22. Governing law and disputes</h2>
          <p style={pStyle}>
            These Terms are governed by the laws applicable to Oxide Digital Technology, without
            regard to conflict-of-law rules.
          </p>
          <p style={pStyle}>
            Any dispute arising out of or relating to these Terms or the Platform will be subject to
            the courts of competent jurisdiction, unless mandatory consumer protection laws in your
            country require otherwise.
          </p>
          <p style={pStyle}>
            If you are a consumer in the European Union, you may also have the right to use approved
            online dispute resolution platforms.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>23. General</h2>
          <ul style={ulStyle}>
            <li>
              <strong>Entire agreement:</strong> These Terms, together with the Privacy Policy and
              any additional terms presented at booking or checkout, form the entire agreement
              between you and Posa regarding the Platform.
            </li>
            <li>
              <strong>Severability:</strong> If any provision is unenforceable, the remaining
              provisions remain in effect.
            </li>
            <li>
              <strong>No waiver:</strong> Failure to enforce a provision is not a waiver of that
              provision.
            </li>
            <li>
              <strong>Assignment:</strong> You may not assign your rights under these Terms without
              our consent. We may assign our rights as part of a merger, acquisition, or sale of
              assets.
            </li>
            <li>
              <strong>Language:</strong> If these Terms are translated, the English version controls
              in case of conflict, unless local law requires otherwise.
            </li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={h2Style}>24. Contact us</h2>
          <p style={pStyle}>If you have questions about these Terms, contact:</p>
          <p style={{ ...pStyle, marginBottom: 0 }}>
            <strong>Oxide Digital Technology</strong><br />
            Email:{' '}
            <a href="mailto:support@oxide-digital.com" style={linkStyle}>support@oxide-digital.com</a>
            <br />
            Website:{' '}
            <a href="https://www.posasport.com" style={linkStyle}>https://www.posasport.com</a>
            <br />
            Company:{' '}
            <a
              href="https://www.oxide-digital.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://www.oxide-digital.com/
            </a>
          </p>
        </section>

        <div style={{
          marginTop: '48px',
          paddingTop: '24px',
          borderTop: '1px solid #e5e7eb',
          lineHeight: '1.6',
          color: '#475569'
        }}>
          <p style={{ margin: 0 }}>
            The Posa platform is developed and maintained by Oxide Digital Technology. Copyright in
            Posa Sport, Posa Studio and this website belongs to{' '}
            <a
              href="https://www.oxide-digital.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              Oxide Digital Technology
            </a>
            .
          </p>
        </div>

        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
          <Link
            to="/"
            style={{
              color: '#007AFF',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </main>
  );
};

export default TermsAndConditions;
